/**
 * Section Tour Definitions
 *
 * This file contains all section-level (tab-level) tour definitions.
 * Each section tour explains what the section does and how to use its key features.
 */
import { SHOW_SUPPLIERS_REQUIRING_ATTENTION } from '../components/dashboard/dashboardSectionVisibility';

export interface SectionTourStep {
  id: string;
  target: string; // CSS selector for the element to highlight
  title: string;
  content: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

export interface SectionTourConfig {
  pageKey: string;
  sectionKey: string;
  title: string;
  steps: SectionTourStep[];
}

// ============================================================================
// DASHBOARD SECTION TOURS
// ============================================================================

export const DASHBOARD_DEFAULT_TOUR: SectionTourConfig = {
  pageKey: 'dashboard',
  sectionKey: 'default',
  title: 'Default Dashboard',
  steps: [
    {
      id: 'kpi-cards',
      target: '[data-tour="dashboard-kpis"]',
      title: 'Key Performance Indicators',
      content: 'These cards show your most important metrics: the configured quality-defect rate (PPM or PPK), OTIF (delivery accuracy), OTD (on-time delivery), and active NCRs. Green indicates targets are met, red means attention needed.',
      placement: 'bottom',
    },
    // CR-8235: the section is hidden, and a step whose target is missing
    // stalls the tour, so the step follows the section's flag.
    ...(SHOW_SUPPLIERS_REQUIRING_ATTENTION ? [{
      id: 'supplier-rankings',
      target: '[data-tour="supplier-rankings"]',
      title: 'Suppliers Requiring Attention',
      content: 'These cards highlight suppliers with poor performance. Click on any supplier to drill down into details and understand the root cause of issues.',
      placement: 'top' as const,
    }] : []),
    {
      id: 'ppm-chart',
      target: '[data-tour="ppm-chart"]',
      title: 'Defect Rate Trend Chart',
      content: 'Track your defect rate (PPM or PPK per platform setting) over time. Click any data point to drill down into specific month details. The orange dashed line shows the trend.',
      placement: 'top',
    },
    {
      id: 'otif-chart',
      target: '[data-tour="otif-chart"]',
      title: 'OTIF Performance',
      content: 'On Time In Full percentage shows how many orders were delivered correctly and on schedule. The stacked bars show OTIF, Arrears, and Failed lines. Click to drill down.',
      placement: 'top',
    },
  ],
};

export const DASHBOARD_GROUP_KPI_TOUR: SectionTourConfig = {
  pageKey: 'dashboard',
  sectionKey: 'group-kpi',
  title: 'Group KPI Dashboard',
  steps: [
    {
      id: 'group-comparison',
      target: '[data-tour="group-comparison"]',
      title: 'Internal vs External Comparison',
      content: 'Compare performance between internal supplier groups and external suppliers. This helps identify whether in-house or outsourced suppliers perform better.',
      placement: 'bottom',
    },
    {
      id: 'group-metrics',
      target: '[data-tour="group-metrics"]',
      title: 'Group Performance Metrics',
      content: 'View aggregated KPIs for each supplier group including defect rate (PPM/PPK), OTIF, and OTD. Use these to identify which groups need improvement.',
      placement: 'top',
    },
  ],
};

export const DASHBOARD_SITE_KPI_TOUR: SectionTourConfig = {
  pageKey: 'dashboard',
  sectionKey: 'site-kpi',
  // CR-5453: relabel to "Site KPI Dashboard" to match the tab nav.
  title: 'Site KPI Dashboard',
  steps: [
    {
      id: 'site-selector',
      target: '[data-tour="view-selector"]',
      title: 'Company Selection',
      content: 'Use the View Selector in the top bar to switch between different companies and see their individual performance metrics.',
      placement: 'bottom',
    },
    {
      id: 'site-comparison',
      target: '[data-tour="site-comparison"]',
      title: 'Company Comparison Charts',
      content: 'Compare OTIF, delivery, and defect-rate (PPM/PPK) performance across all your companies. Identify which companies are performing well and which need attention.',
      placement: 'top',
    },
    {
      id: 'ncr-breakdown',
      target: '[data-tour="ncr-breakdown"]',
      title: 'NCR Breakdown',
      content: 'See how non-conformance reports are distributed across companies. This helps identify quality hotspots.',
      placement: 'top',
    },
  ],
};

export const DASHBOARD_COST_TOUR: SectionTourConfig = {
  pageKey: 'dashboard',
  sectionKey: 'cost',
  title: 'Cost Dashboard',
  steps: [
    {
      id: 'spend-overview',
      target: '[data-tour="spend-overview"]',
      title: 'Spend Overview',
      content: 'View your total spend, broken down by time period and category. Track spending trends to identify cost-saving opportunities.',
      placement: 'bottom',
    },
    {
      id: 'arrears-value',
      target: '[data-tour="arrears-value"]',
      title: 'Historical Arrears',
      content: 'Monitor the value of orders in arrears. High arrears indicate supply chain issues that may impact production.',
      placement: 'top',
    },
  ],
};

export const DASHBOARD_EMISSIONS_TOUR: SectionTourConfig = {
  pageKey: 'dashboard',
  sectionKey: 'emissions',
  title: 'Emissions Dashboard',
  steps: [
    {
      id: 'carbon-overview',
      target: '[data-tour="carbon-overview"]',
      title: 'Carbon Footprint',
      content: 'Track CO2 emissions from your supply chain activities. This data helps meet sustainability reporting requirements.',
      placement: 'bottom',
    },
    {
      id: 'transport-breakdown',
      target: '[data-tour="transport-breakdown"]',
      title: 'Transport Emissions',
      content: 'See emissions breakdown by transport method (road, air, sea, rail). Identify opportunities to reduce carbon footprint.',
      placement: 'top',
    },
  ],
};

export const DASHBOARD_COMPLIANCE_TOUR: SectionTourConfig = {
  pageKey: 'dashboard',
  sectionKey: 'compliance',
  title: 'Compliance Dashboard',
  steps: [
    {
      id: 'compliance-status',
      target: '[data-tour="compliance-status"]',
      title: 'Compliance Status',
      content: 'Monitor supplier compliance with regulatory requirements, certifications, and quality standards.',
      placement: 'bottom',
    },
    {
      id: 'expiring-certs',
      target: '[data-tour="expiring-certs"]',
      title: 'Expiring Certifications',
      content: 'Track certifications nearing expiry. Proactively manage renewals to avoid supply disruptions.',
      placement: 'top',
    },
  ],
};

// ============================================================================
// PURCHASING SECTION TOURS
// ============================================================================

export const PURCHASING_PO_HEADERS_TOUR: SectionTourConfig = {
  pageKey: 'purchasing',
  sectionKey: 'po_headers',
  title: 'Purchase Order Headers',
  steps: [
    {
      id: 'po-list',
      target: '[data-tour="po-headers-table"]',
      title: 'Purchase Orders List',
      content: 'This table shows all your purchase orders. Each row represents a PO with its status, supplier, total value, and delivery information.',
      placement: 'top',
    },
    {
      id: 'po-status',
      target: '[data-tour="po-status-filter"]',
      title: 'PO Status Filter',
      content: 'Filter POs by status: Draft, Sent, Acknowledged, In Progress, Delivered, or Closed. Use these to quickly find orders needing attention.',
      placement: 'bottom',
    },
    {
      id: 'po-search',
      target: '[data-tour="po-search"]',
      title: 'Search & Filter',
      content: 'Search by PO number, supplier name, or part number. Use the date filters to narrow down results.',
      placement: 'bottom',
    },
  ],
};

export const PURCHASING_PO_LINES_TOUR: SectionTourConfig = {
  pageKey: 'purchasing',
  sectionKey: 'po_lines',
  title: 'Purchase Order Lines',
  steps: [
    {
      id: 'lines-table',
      target: '[data-tour="po-lines-table"]',
      title: 'PO Lines Table',
      content: 'This table shows all individual line items from purchase orders. Each row represents a specific part and quantity ordered from a supplier.',
      placement: 'top',
    },
    {
      id: 'ack-status',
      target: '[data-tour="ack-status-col"]',
      title: 'Acknowledgment Status',
      content: 'Shows whether the supplier has acknowledged the delivery date. New lines need acknowledgment. Click the action button to accept or propose an alternative date.',
      placement: 'left',
    },
    {
      id: 'on-hold',
      target: '[data-tour="on-hold-filter"]',
      title: 'On-Hold Lines',
      content: 'Lines can be put on hold when there are issues. Filter by on-hold status to see lines requiring attention before processing.',
      placement: 'bottom',
    },
    {
      id: 'quick-filters',
      target: '[data-tour="quick-filters"]',
      title: 'Quick Filters',
      content: 'Use these tabs to quickly filter by line status: All lines, Open (in progress), or Delivered.',
      placement: 'bottom',
    },
    {
      id: 'line-actions',
      target: '[data-tour="line-actions"]',
      title: 'Line Actions',
      content: 'Click on any row to view details and take actions: accept dates, propose alternatives, or put lines on hold.',
      placement: 'left',
    },
  ],
};

export const PURCHASING_FORECAST_TOUR: SectionTourConfig = {
  pageKey: 'purchasing',
  sectionKey: 'forecast',
  title: 'Forecast',
  steps: [
    {
      id: 'forecast-table',
      target: '[data-tour="forecast-table"]',
      title: 'Demand Forecast',
      content: 'View future order forecasts to plan capacity and inventory. Forecasts help suppliers prepare for upcoming demand.',
      placement: 'top',
    },
    {
      id: 'forecast-period',
      target: '[data-tour="forecast-period"]',
      title: 'Forecast Period',
      content: 'Select the time period to view forecasts: weekly, monthly, or quarterly views available.',
      placement: 'bottom',
    },
  ],
};

export const PURCHASING_ASN_TOUR: SectionTourConfig = {
  pageKey: 'purchasing',
  sectionKey: 'asn',
  title: 'Advance Ship Notices',
  steps: [
    {
      id: 'asn-list',
      target: '[data-tour="asn-table"]',
      title: 'ASN List',
      content: 'View all Advance Ship Notices from suppliers. ASNs notify you of incoming shipments before they arrive, helping you prepare for receiving.',
      placement: 'top',
    },
    {
      id: 'asn-status',
      target: '[data-tour="asn-status-tabs"]',
      title: 'ASN Status',
      content: 'Track shipment progress through tabs: Created, Submitted, In Transit, Delivered, or Received. Use these to monitor your inbound logistics.',
      placement: 'bottom',
    },
    {
      id: 'tracking',
      target: '[data-tour="tracking-col"]',
      title: 'Tracking Information',
      content: 'View carrier and tracking number for each shipment. Premium features include real-time courier status updates.',
      placement: 'left',
    },
    {
      id: 'create-asn',
      target: '[data-tour="create-asn-btn"]',
      title: 'Create ASN',
      content: 'Suppliers can create ASNs to notify customers of shipments. Click here to create a new ASN with shipment details.',
      placement: 'left',
    },
  ],
};

export const PURCHASING_DELIVERY_TOUR: SectionTourConfig = {
  pageKey: 'purchasing',
  sectionKey: 'delivery',
  title: 'Deliveries & GRNs',
  steps: [
    {
      id: 'grn-list',
      target: '[data-tour="grn-table"]',
      title: 'Goods Receipt Notes',
      content: 'GRNs record what was actually received. Compare against what was ordered to identify discrepancies and quality issues.',
      placement: 'top',
    },
    {
      id: 'qty-received',
      target: '[data-tour="qty-col"]',
      title: 'Quantities',
      content: 'See received, accepted, and rejected quantities. Discrepancies between ordered and received quantities are flagged here.',
      placement: 'left',
    },
    {
      id: 'grn-actions',
      target: '[data-tour="grn-actions"]',
      title: 'GRN Actions',
      content: 'Acknowledge receipt, dispute discrepancies, or create NCRs for quality issues directly from this view.',
      placement: 'left',
    },
  ],
};

export const PURCHASING_RFQ_TOUR: SectionTourConfig = {
  pageKey: 'purchasing',
  sectionKey: 'rfq',
  title: 'Request for Quotation',
  steps: [
    {
      id: 'rfq-list',
      target: '[data-tour="rfq-table"]',
      title: 'RFQ List',
      content: 'View and manage all Request for Quotations. RFQs allow you to request pricing from multiple suppliers for comparison.',
      placement: 'top',
    },
    {
      id: 'create-rfq',
      target: '[data-tour="create-rfq-btn"]',
      title: 'Create RFQ',
      content: 'Start a new RFQ to request quotes from suppliers. Specify parts, quantities, and delivery requirements.',
      placement: 'left',
    },
  ],
};

export const PURCHASING_CONTRACT_PRICING_TOUR: SectionTourConfig = {
  pageKey: 'purchasing',
  sectionKey: 'contract_pricing',
  title: 'Pricebooks',
  steps: [
    {
      id: 'pricing-table',
      target: '[data-tour="pricing-table"]',
      title: 'Contract Prices',
      content: 'View negotiated contract prices with suppliers. These prices are used to validate PO pricing and track price changes.',
      placement: 'top',
    },
    {
      id: 'price-history',
      target: '[data-tour="price-history"]',
      title: 'Price History',
      content: 'See historical price changes to track inflation and negotiate better rates.',
      placement: 'left',
    },
  ],
};

export const PURCHASING_CONCESSIONS_TOUR: SectionTourConfig = {
  pageKey: 'purchasing',
  sectionKey: 'concessions',
  title: 'Production Permits / Concessions',
  steps: [
    {
      id: 'concessions-list',
      target: '[data-tour="concessions-table"]',
      title: 'Concessions List',
      content: 'Manage production permits and concession requests. These allow use of non-conforming materials under controlled conditions.',
      placement: 'top',
    },
    {
      id: 'concession-status',
      target: '[data-tour="concession-status"]',
      title: 'Approval Status',
      content: 'Track concession approvals: Pending, Approved, Rejected, or Expired. Only approved concessions can be used.',
      placement: 'left',
    },
  ],
};

// ============================================================================
// QUALITY SECTION TOURS
// ============================================================================

export const QUALITY_NCR_TOUR: SectionTourConfig = {
  pageKey: 'quality',
  sectionKey: 'ncrs',
  title: 'Non-Conformance Reports',
  steps: [
    {
      id: 'ncr-table',
      target: '[data-tour="ncr-table"]',
      title: 'NCR List',
      content: 'All non-conformance reports are listed here. NCRs track quality issues with received goods and require supplier corrective action.',
      placement: 'top',
    },
    {
      id: 'ncr-status',
      target: '[data-tour="ncr-status-col"]',
      title: 'NCR Status',
      content: 'Status workflow: Draft, Created (with supplier, no action yet), CA Submitted (supplier did CA), CAR Submitted (supplier did CAR), then Closed. Follow the workflow to resolution.',
      placement: 'left',
    },
    {
      id: 'next-action',
      target: '[data-tour="next-action-col"]',
      title: 'Next Action',
      content: 'Shows who needs to take action: "Awaiting Supplier" means the supplier needs to respond, "Awaiting Customer" means you need to review their response.',
      placement: 'left',
    },
    {
      id: 'add-ncr',
      target: '[data-tour="add-ncr-btn"]',
      title: 'Create NCR',
      content: 'Click ADD to raise a new NCR when you identify a quality issue with received goods. Complete the form with defect details.',
      placement: 'left',
    },
    {
      id: 'filter-panel',
      target: '[data-tour="ncr-filters"]',
      title: 'Filter NCRs',
      content: 'Use the Filter button to search and filter NCRs by status, date range, supplier, or part number.',
      placement: 'bottom',
    },
  ],
};

// ============================================================================
// SUPPLIERS SECTION TOURS
// ============================================================================

export const SUPPLIERS_LIST_TOUR: SectionTourConfig = {
  pageKey: 'suppliers',
  sectionKey: 'list',
  title: 'Supplier Management',
  steps: [
    {
      id: 'status-sidebar',
      target: '[data-tour="status-sidebar"]',
      title: 'Status Filter',
      content: 'Filter suppliers by their onboarding status: Draft, Pending Approval, Invited, Due Diligence, Approved, or Not Approved. The badge shows count for each status.',
      placement: 'right',
    },
    {
      id: 'supplier-map',
      target: '[data-tour="supplier-map"]',
      title: 'Supplier Locations',
      content: 'Interactive map showing where your suppliers are located. Click markers to see supplier details and navigate to their profile.',
      placement: 'bottom',
    },
    {
      id: 'supplier-cards',
      target: '[data-tour="supplier-cards"]',
      title: 'Supplier Cards',
      content: 'Each card shows a supplier with their current status and progress. Click to view full details, questionnaires, and performance data.',
      placement: 'top',
    },
    {
      id: 'add-supplier',
      target: '[data-tour="add-supplier-btn"]',
      title: 'Add New Supplier',
      content: 'Start the supplier onboarding process by clicking Add Supplier. You will be guided through the approval workflow.',
      placement: 'left',
    },
  ],
};

export const SUPPLIERS_ONBOARDING_TOUR: SectionTourConfig = {
  pageKey: 'suppliers',
  sectionKey: 'onboarding',
  title: 'Supplier Onboarding',
  steps: [
    {
      id: 'onboarding-dashboard',
      target: '[data-tour="onboarding-dashboard"]',
      title: 'Onboarding Overview',
      content: 'Track all supplier onboarding activities. See pending questionnaires, approvals needed, and recently onboarded suppliers.',
      placement: 'top',
    },
    {
      id: 'pending-approvals',
      target: '[data-tour="pending-approvals"]',
      title: 'Pending Approvals',
      content: 'Suppliers waiting for your review. Click to view their questionnaire responses and approve or reject their onboarding.',
      placement: 'right',
    },
    {
      id: 'questionnaire-status',
      target: '[data-tour="questionnaire-status"]',
      title: 'Questionnaire Status',
      content: 'See which onboarding questionnaires have been completed by suppliers and which are still outstanding.',
      placement: 'left',
    },
  ],
};

// ============================================================================
// ALL SECTION TOURS MAP
// ============================================================================

export const SECTION_TOURS: Record<string, Record<string, SectionTourConfig>> = {
  dashboard: {
    default: DASHBOARD_DEFAULT_TOUR,
    'group-kpi': DASHBOARD_GROUP_KPI_TOUR,
    'site-kpi': DASHBOARD_SITE_KPI_TOUR,
    cost: DASHBOARD_COST_TOUR,
    emissions: DASHBOARD_EMISSIONS_TOUR,
    compliance: DASHBOARD_COMPLIANCE_TOUR,
  },
  purchasing: {
    po_headers: PURCHASING_PO_HEADERS_TOUR,
    po_lines: PURCHASING_PO_LINES_TOUR,
    forecast: PURCHASING_FORECAST_TOUR,
    asn: PURCHASING_ASN_TOUR,
    delivery: PURCHASING_DELIVERY_TOUR,
    rfq: PURCHASING_RFQ_TOUR,
    contract_pricing: PURCHASING_CONTRACT_PRICING_TOUR,
    concessions: PURCHASING_CONCESSIONS_TOUR,
  },
  quality: {
    ncrs: QUALITY_NCR_TOUR,
  },
  suppliers: {
    list: SUPPLIERS_LIST_TOUR,
    onboarding: SUPPLIERS_ONBOARDING_TOUR,
  },
};

/**
 * Get a section tour configuration
 */
export function getSectionTour(pageKey: string, sectionKey: string): SectionTourConfig | null {
  return SECTION_TOURS[pageKey]?.[sectionKey] || null;
}

/**
 * Get all section tours for a page
 */
export function getPageSectionTours(pageKey: string): SectionTourConfig[] {
  const pageTours = SECTION_TOURS[pageKey];
  return pageTours ? Object.values(pageTours) : [];
}
